/* ==========================================================================
   B&B – Carousel (bb-carousel.js)
   Turns a [data-carousel-list] scroller into a carousel:
   - prev/next buttons ([data-carousel-prev] / [data-carousel-next]) scroll a page
   - optional infinite loop, separately for desktop and mobile
     (data-loop-desktop / data-loop-mobile = "true")
   - works with a 2-row mobile grid (data-mobile-rows="2")
   - only cards visible at the current screen size are used
   Loop works by adding copies of the cards before and after the real ones and
   quietly jumping back by one set when you scroll into the copies.
   ========================================================================== */

(() => {
  if (customElements.get('bb-carousel')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BBCarousel extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-carousel-list]');
      if (!this.list) return;
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.mq = window.matchMedia('(max-width: 767px)');
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.prevBtn?.addEventListener('click', () => this.page(-1), opts);
      this.nextBtn?.addEventListener('click', () => this.page(1), opts);
      this.list.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.abort.signal });
      this.mq.addEventListener('change', () => this.build(), opts);

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.build(), 200);
      }, opts);

      // Theme editor: selecting a card scrolls it into view
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.list.contains(e.target)) return;
        this.build(false);
        this.scrollToItem(e.target, false);
      }, opts);

      requestAnimationFrame(() => this.build());
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.scrollTimer);
    }

    originals() {
      return Array.from(this.list.children).filter((el) => !el.hasAttribute('data-clone'));
    }

    isShown(el) {
      return getComputedStyle(el).display !== 'none';
    }

    clearClones() {
      this.list.querySelectorAll('[data-clone]').forEach((el) => el.remove());
      this.looping = false;
    }

    makeClone(el) {
      const clone = el.cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.removeAttribute('data-shopify-editor-block');
      clone.classList.remove('bb-reveal');
      clone.style.removeProperty('--bb-reveal-delay');
      clone.querySelectorAll('a, button').forEach((n) => n.setAttribute('tabindex', '-1'));
      return clone;
    }

    offsetOf(el) {
      const listRect = this.list.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(this.list).paddingLeft) || 0;
      return el.getBoundingClientRect().left - listRect.left - pad + this.list.scrollLeft;
    }

    build(keepPosition = true) {
      this.clearClones();

      const mobile = this.mq.matches;
      const wantLoop = (mobile ? this.dataset.loopMobile : this.dataset.loopDesktop) === 'true';
      const items = this.originals().filter((el) => this.isShown(el));
      const scrollable = this.list.scrollWidth > this.list.clientWidth + 2;

      this.classList.toggle('is-scrollable', scrollable);

      if (!scrollable || !wantLoop || items.length < 2) {
        if (!keepPosition) this.list.scrollLeft = 0;
        this.updateArrows();
        return;
      }

      // In a 2-row grid an odd number of cards would shift the columns at the
      // join, so the set is doubled to keep every column a matching pair.
      const twoRows = mobile && this.dataset.mobileRows === '2';
      const odd = twoRows && items.length % 2 === 1;
      const unit = odd ? items.concat(items) : items;

      // Middle: real cards (+ one copy set if odd). Before/after: copy sets.
      const lastReal = items[items.length - 1];
      if (odd) {
        let anchor = lastReal;
        items.forEach((el) => {
          const c = this.makeClone(el);
          anchor.after(c);
          anchor = c;
        });
      }
      const firstReal = items[0];
      unit.forEach((el) => this.list.insertBefore(this.makeClone(el), this.list.firstChild));
      // Re-order the "before" copies to match the real order
      const before = Array.from(this.list.children).slice(0, unit.length).reverse();
      before.forEach((el) => this.list.insertBefore(el, firstReal));
      unit.forEach((el) => this.list.appendChild(this.makeClone(el)));

      const firstBefore = this.list.firstElementChild;
      this.unitWidth = this.offsetOf(firstReal) - this.offsetOf(firstBefore);
      this.midStart = this.offsetOf(firstReal);
      this.looping = true;
      this.jumpTo(this.midStart);
      this.updateArrows();
    }

    jumpTo(x) {
      const prev = this.list.style.scrollBehavior;
      this.list.style.scrollBehavior = 'auto';
      this.list.scrollLeft = x;
      this.list.style.scrollBehavior = prev;
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        if (this.looping && this.unitWidth > 0) {
          const x = this.list.scrollLeft;
          if (x < this.midStart - this.unitWidth / 2) {
            this.jumpTo(x + this.unitWidth);
          } else if (x >= this.midStart + this.unitWidth / 2) {
            this.jumpTo(x - this.unitWidth);
          }
        }
        this.updateArrows();
      }, 120);
    }

    // Arrow step: one card by default, or a full screenful with data-step="page"
    page(dir) {
      let distance = this.list.clientWidth;
      if (this.dataset.step !== 'page') {
        const card = Array.from(this.list.children).find((el) => this.isShown(el));
        if (card) {
          const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
          distance = card.getBoundingClientRect().width + gap;
        }
      }
      this.list.scrollBy({ left: dir * distance, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    scrollToItem(el, smooth = true) {
      this.list.scrollTo({ left: this.offsetOf(el), behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    }

    updateArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      if (this.looping) {
        this.prevBtn && (this.prevBtn.disabled = false);
        this.nextBtn && (this.nextBtn.disabled = false);
        return;
      }
      const x = this.list.scrollLeft;
      const max = this.list.scrollWidth - this.list.clientWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = x <= 2;
      if (this.nextBtn) this.nextBtn.disabled = x >= max;
    }
  }

  customElements.define('bb-carousel', BBCarousel);
})();